import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  Creature,
  Potence,
  CharacterSheetInterface,
  Transform,
  CharacterSpell,
  Attack,
  CreatureTransform,
  ExtraEffectsFeature,
  CharacterFeature,
  ExtraEffectsOfTypeFeature,
  SkillAsAbility,
  StoredSpell,
} from "@dndModels";
import { ModelBase, SmartHash } from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  effect: AbilityEffect;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  ee_cf?: CharacterFeature;
  type_cf?: CharacterFeature;
  shared: boolean;
  level: number;
  wide: boolean;
  show_extra_effects?: boolean;
  attack_mod?: number;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onCostChange: (costs: { [resource: string]: number }) => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterTransformEffect({
  character,
  char_interface,
  spell,
  effect,
  processing,
  disabled,
  log_it = false,
  ee_cf,
  type_cf,
  shared,
  level,
  wide,
  show_extra_effects = false,
  attack_mod = 0,
  casting_time,
  // onChange,
  onCostChange,
  // onTransform
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [transforms, setTransforms] = useState<Transform[]>([]);
  const [is_processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [selected_id, setSelectedId] = useState("");
  const [selected_creature, setSelectedCreature] = useState<Creature | null>(null);
  // const [creatures, setCreatures] = useState<Creature[]>([]);

   
  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));
  const item_id = spell instanceof CharacterAbility ? spell.item_id : (spell instanceof CharacterSpell ? spell.source_true_id : "");
   
  useEffect(() => {
    if (spell instanceof CharacterAbility && spell.the_ability) {
      const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
        char_util.getPotence({slot_level: level, char: character, item_id, char_int: char_interface, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, log_it}) 
        : spell ? 
        char_util.getPotence({slot_level: level, char: character, item_id, char_int: char_interface, spell, effect, cfs_id: "", attack_mod, log_it}) : null;
      if (potence) {
        const transform_ids = potence.transform_options.map(o2 => o2.transform_id);
        const filtered: Transform[] = transforms.filter(o => transform_ids.includes(o._id)); // api.filter("", transforms, potence.obj_filter);
        // if (potence.knowledge_usage_key.length > 0) {
        //   filtered = filtered.filter(o => 
        //     character.knowledge.filter(o2 => 
        //       o2.obj_id === o._id && 
        //       o2.type === "Transform" && 
        //       o2.usage === potence.knowledge_usage_key
        //     ).length > 0
        //   );
        // }
        const selected_transform = filtered.length === 1 ? filtered[0] : filtered.find(o => o._id === selected_id);
        if (selected_transform) {
          const costs: { [resource: string]: number } = {};
          if (spell.the_ability instanceof SkillAsAbility) {
            costs[spell.the_ability.resource_consumed] = selected_transform.cost;
          } else {
            costs[spell.the_ability.consumed_resource.resource_consumed] = selected_transform.cost;
          }
          onCostChange(costs);
        }
      }
    }
    // eslint-disable-next-line
  }, [selected_id, setSelectedId]);
      
  const load = () => {
    // TODO: change this to use char_util.transforms and char_util.getCreatures.  Will require adding char_util.transforms
    setLoading(true);
    const onComplete = (res: SmartHash) => {
      if (res && !res.error) {
        setTransforms(res.transform as Transform[]);
        // setCreatures(res.creature as Creature[]);
        setLoading(false);
      } 
    };
    const onProgress = () => {};
    api.getSetOfObjectsWithCallbacks({ 
      data_types: ["transform","creature"],
      onComplete, onProgress 
    });
    setFetched(true);
  }

  const renderCreatureTransform = (filtered: Transform[], selected_transform: Transform | undefined) => {
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({slot_level: level, char: character, item_id, char_int: char_interface, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, log_it}) 
      : spell ? 
      char_util.getPotence({slot_level: level, char: character, item_id, char_int: char_interface, spell, effect, cfs_id: "", attack_mod, log_it}) : null;
    // debugger;
    if (potence && selected_transform instanceof CreatureTransform) {
      let creature_filter: any = {};
      if (selected_transform.knowledge_type === "Creature" && 
        selected_transform.knowledge_usage_key !== "") {
        const char_knowledge = character.knowledge.filter(o => o.type === "Creature" && o.usage === selected_transform.knowledge_usage_key);
        // console.log(char_knowledge);
        creature_filter["_id*ObjectIDs"] = char_knowledge.map(o => o.obj_id);
      } else {
        creature_filter = char_util.getCreatureFilter(character, selected_transform, spell);
      }
      // debugger;
      return (
        <>
          { filtered.length === 1 && !reloading ? 
            <>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <SelectObjectBox
                  name={ selected_transform.name }
                  data_type="creature"
                  filter={creature_filter} 
                  // internal_filter
                  display_format="{obj.true_name} (CR: {obj.challenge_rating}){obj.flying_string}"
                  sort={(obj1: ModelBase, obj2: ModelBase) => {
                    if (obj1 instanceof Creature && obj2 instanceof Creature) {
                      return obj1.challenge_rating > obj2.challenge_rating ? 1 : (obj1.challenge_rating === obj2.challenge_rating ? 0 : -1);
                    }
                    return 0;
                  }}
                  value={selected_creature ? selected_creature._id : ""}
                  auto_select_on_1
                  onChangeObject={setSelectedCreature}
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <DandDButtonBox
                  name="Transform"
                  style={{ fontSize: "9" }} 
                  image={ effect.type }
                  disabled={ !enabled || selected_creature === null }
                  onClick={() => {
                    const transform_id = filtered.length === 1 ? filtered[0]._id : selected_id;
                    const transform = transforms.find(o => o._id === transform_id);
                    if (transform instanceof CreatureTransform && selected_creature) {
                      // Apply the transform
                      // debugger;
                      char_util.createTransformInstance(character, transform, level, selected_creature).then(res => {
                        character.changed();
                        char_util.repopulateCharFeatureBases(character);
                        setPing(!ping);
                      });
                    }
                  }} 
                />
              </Grid>
            </>
          : !reloading && 
            <>
              <Grid
                className="centered"
                size={{
                  xs: 4
                }}>
                <DandDButtonBox
                  name="Transform"
                  style={{ fontSize: "9" }} 
                  image={ effect.type }
                  disabled={ !enabled || selected_creature === null }
                  onClick={() => {
                    const transform_id = filtered.length === 1 ? filtered[0]._id : selected_id;
                    const transform = transforms.find(o => o._id === transform_id);
                    if (transform instanceof CreatureTransform && selected_creature) {
                      // Apply the transform
                      char_util.createTransformInstance(character, transform, level, selected_creature).then(res => {
                        character.changed();
                        char_util.repopulateCharFeatureBases(character);
                        setPing(!ping);
                      });
                    }
                  }} 
                />
              </Grid>
              <Grid
                className="centered"
                size={{
                  xs: 12
                }}>
                <SelectObjectBox
                  name={ selected_transform.name }
                  data_type="creature"
                  filter={creature_filter} 
                  display_format="{obj.true_name} (CR: {obj.challenge_rating}){obj.flying_string}"
                  value={selected_creature ? selected_creature._id : ""}
                  onChangeObject={setSelectedCreature}
                />
              </Grid>
            </>
          }
        </>
      );
    }
    return null;
  }

  const renderTransformControls = (potence: Potence, extra_string: string) => {
    // if (spell instanceof CharacterAbility && spell.name.includes("Drink")) {
    //   debugger;
    // }
    const transform_ids = potence.transform_options.map(o2 => o2.transform_id);
    const filtered: Transform[] = transforms.filter(o => transform_ids.includes(o._id)); // api.filter("", transforms, potence.obj_filter);
    if (filtered.length === 1 && filtered[0] instanceof CreatureTransform) {
      // Knowledge is about the Creatures rather than the transforms
      // Handled later.
    } else {
      // if (potence.knowledge_usage_key.length > 0) {
      //   filtered = filtered.filter(o => 
      //     character.knowledge.filter(o2 => 
      //       o2.obj_id === o._id && 
      //       o2.type === "Transform" && 
      //       o2.usage === potence.knowledge_usage_key
      //     ).length > 0
      //   );
      // }
    }
    const selected_transform = filtered.length === 1 ? filtered[0] : filtered.find(o => o._id === selected_id);
    const selected_transform_id = selected_transform ? selected_transform._id : "";
    const range_string = spell instanceof Attack ? "Self" : char_util.getRangeString({ char_int: character, spell, effect });
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="centered"
          size={{
            xs: 4
          }}>
          { range_string }{ extra_string !== "" ? (range_string !== "" ? ` / ${extra_string}` : extra_string) : "" }
        </Grid>
        { (filtered.length > 1 || !(selected_transform instanceof CreatureTransform)) &&
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            { filtered.length === 1 ? 
              <DisplayObjects type="transform" ids={[selected_transform_id]} /> 
            :
              <SelectObjectBox
                name="Transform"
                data_type="transform"
                filter={{ "_id": { "in": filtered.map(o => o._id) }}} 
                value={selected_id}
                onChange={setSelectedId}
              />
            }
          </Grid> 
        }
        { selected_transform instanceof CreatureTransform ? 
          renderCreatureTransform(filtered, selected_transform)
        :
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            <DandDButtonBox
              name="Transform"
              style={{ fontSize: "9" }} 
              image={ effect.type }
              disabled={ !enabled || (filtered.length > 1 && selected_id === "") }
              onClick={() => {
                const transform_id = filtered.length === 1 ? filtered[0]._id : selected_id;
                const transform = transforms.find(o => o._id === transform_id);
                if (transform) {
                  // Apply the transform
                  char_util.createTransformInstance(character, transform, level).then(res => {
                    character.changed();
                    char_util.repopulateCharFeatureBases(character);
                    setPing(!ping);
                  });
                }
              }} 
            />
          </Grid>
        }
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell: ee_cf, item_id, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod, log_it }) : 
      spell ? char_util.getPotence({ slot_level: level, char: character, char_int: char_interface, spell, effect, item_id, cfs_id: "", attack_mod, log_it }) : null;
    let extra_string = "";
    if (potence) {
      extra_string = potence.extra;
        return renderTransformControls(potence, extra_string);
    }
    return <></>;
  }
}
